import { zodResolver } from '@hookform/resolvers/zod'
import { SmartCaptcha } from '@yandex/smart-captcha'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import cl from '@/screens/service/ui/service-form/form/form.module.scss'

import { NewsletterCheckbox, useAssent } from '@/entities/assent'
import { fetchDiscountRequest, submitDiscountMindboxRegister } from '@/entities/b24'
import { useUser } from '@/entities/user'

import { bitrixChat } from '@/shared/integrations/BitrixChat'
import { sendYandexMetricaEvent } from '@/shared/integrations/yandex-metric/utils/send-metric-event'
import { BASE_URL, YANDEX_CAPTCHA_KEY } from '@/shared/lib/const/env'
import { createMarkup } from '@/shared/lib/utils/common/createMarkup'
import { getCookie } from '@/shared/lib/utils/common/getCookie'
import { formatPrice } from '@/shared/lib/utils/price/format-price'
import { emailSchemaOptional, normalizeEmail } from '@/shared/lib/validation/email'
import { formatPhoneMasked, phoneSchema } from '@/shared/lib/validation/phone'
import { TDiscountProductItem } from '@/shared/types/deprecated-types'
import { Button, Col, Input, RHFEmailInput, RHFPhoneInput, Row } from '@/shared/ui'
import { AgreementBlock } from '@/shared/ui/AgreementBlock/AgreementBlock'
import { FormSuccessMessage } from '@/shared/ui/FormSuccessMessage'
import { Image } from '@/shared/ui/Image'
import { SegmentedControl } from '@/shared/ui/segmented-control'
import { TextArea } from '@/shared/ui/TextArea/TextArea'

import styles from './DiscountModal.module.scss'

const discountFormSchema = z.object({
	NAME: z
		.string({ required_error: 'Введите имя' })
		.min(2, 'Имя должно содержать минимум 2 символа')
		.regex(/^(?!\s*$).+/, 'Имя не может состоять только из пробелов'),
	EMAIL: emailSchemaOptional,
	PHONE: phoneSchema,
	COMMENT: z
		.string()
		.optional()
		.transform(v => (!v?.trim() ? 'Не указан' : v))
})

type FormValues = z.infer<typeof discountFormSchema>

export type DiscountModalProps = TDiscountProductItem & {
	onOk: () => void
	onCancel: () => void
	submitId?: string
}

export const DynamicDiscountModal = dynamic(
	() => import(`src/screens/Discount/page/ui/DiscountModal/DiscountModal`).then(({ DiscountModal }) => DiscountModal),
	{
		ssr: false
	}
)

export const DiscountModal = (props: DiscountModalProps): JSX.Element => {
	const {
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { errors }
	} = useForm<FormValues>({
		mode: 'onBlur',
		resolver: zodResolver(discountFormSchema),
		defaultValues: {
			NAME: '',
			EMAIL: '',
			PHONE: '',
			COMMENT: ''
		}
	})

	const { user, isLoading: userInfoIsLoading } = useUser()
	const {
		showNewsletterCheckbox,
		isAccepted: isNewsletterAccepted,
		setIsAccepted: setIsNewsletterAccepted,
		checkConsent,
		saveConsent,
		hasConsent
	} = useAssent()

	const phone = watch('PHONE')

	useEffect(() => {
		checkConsent(phone)
	}, [phone])

	const [userType, setUserType] = useState(`individual`)
	const [successMessageIsVisible, setSuccessMessageIsVisible] = useState(false)
	const [errorMessage, setErrorMessage] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isDisabled, setIsDisabled] = useState(false)
	const [captcha, setCaptcha] = useState<string>('')
	const clientSourceCookie = getCookie('_client_source')
	const pathname = usePathname()

	useEffect(() => {
		if (!user) return

		setValue('NAME', user.first_name)
		setValue('PHONE', formatPhoneMasked(user.user_phone))

		if (user.user_email && user.user_email !== '0') {
			setValue('EMAIL', normalizeEmail(user.user_email))
		}
	}, [user, setValue])

	const isLegalUser = userType === `legal`

	const getPopupUrl = (): string => {
		if (typeof window === 'undefined') {
			const fallbackUrl = new URL(`https://www.iport.ru${pathname}`)
			fallbackUrl.searchParams.set('ref_open_modal', String(props.ID))
			return fallbackUrl.toString()
		}

		const currentUrl = new URL(window.location.href)
		currentUrl.searchParams.set('ref_open_modal', String(props.ID))

		return currentUrl.toString()
	}

	const onSubmit = async (values: FormValues) => {
		if (!captcha) return
		setIsLoading(true)
		const pageUrl = getPopupUrl()
		const stockName = (props.STOCK_NAME || '')
			.replace(/<[^>]*>/g, ' ')
			.replace(/\s+/g, ' ')
			.trim()
		const stockSendText = `${props.IS_SERVICE ? 'Сервисный центр ' : ''}${stockName}`.trim()

		const formData = {
			NAME: values.NAME,
			EMAIL: values.EMAIL ?? '',
			PHONE: values.PHONE,
			COMMENT: values.COMMENT,
			PRICE: props.PRICE,
			ITEM: props.TITLE,
			PART_NUMBER: props.PART_NUMBER,
			STOCK: props.STOCK_NAME,
			DISCOUNT_KOD: props.DISCOUNT_KOD,
			DISCOUNT_TYPE: props.DISCOUNT_TYPE,
			TYPE: `Discounted`,
			STOCK_CODE: props.STOCK_CODE,
			SCRIPT_ID: isLegalUser ? `665327` : `442409`,
			recaptcha_response: captcha
		}

		try {
			await fetchDiscountRequest(formData, clientSourceCookie)
			if (!hasConsent && isNewsletterAccepted) {
				await saveConsent(values.PHONE)
			}

			const normalizedPhone = values.PHONE.replace(/\)+|\s+|-+|\(+/gm, ``)

			void submitDiscountMindboxRegister({
				NAME: values.NAME,
				PHONE: normalizedPhone,
				EMAIL: formData.EMAIL || '',
				TITLE: 'Discount',
				URL: pageUrl,
				SEND_TEXT: stockSendText,
				IS_INDIVIDUAL: isLegalUser ? 'false' : 'true'
			}).catch(error => {
				console.error('Ошибка отправки discount mindbox register:', error)
			})

			setSuccessMessageIsVisible(true)
			sendYandexMetricaEvent(`send_a_discount_request`)
		} catch (err) {
			setIsLoading(false)
			setIsDisabled(true)
			setErrorMessage(true)
		}
	}
	const onChange = useCallback(
		(value: string) => {
			setCaptcha(value)
		},
		[setCaptcha]
	)
	const userOptions = [
		{ label: 'Физическое лицо', value: 'individual' },
		{ label: 'Юридическое лицо', value: 'legal' }
	]

	const handleUserChange = (value: any): any => {
		setUserType(value)
	}

	return (
		<aside className={styles.modal}>
			{successMessageIsVisible ? (
				<FormSuccessMessage onOk={props.onOk} />
			) : (
				<>
					<header className={styles.header}>
						<h3 className={styles.title}>Заявка на устройство</h3>
						<SegmentedControl
							className={cl.tabs}
							size="sm"
							options={userOptions}
							value={userType}
							onChange={handleUserChange}
						/>
					</header>
					<div className={styles.info}>
						<div className={styles.image}>
							<Image src={BASE_URL + props.IMAGES[0]} width={100} height={100} alt={props.NAME} />
						</div>
						<div className={styles.itemTitle}>
							<h2>{props.TITLE}</h2>
							<p className={styles.stock}>
								Самовывоз из <span dangerouslySetInnerHTML={createMarkup(props.STOCK_NAME)} />
							</p>
						</div>

						<span className={styles.price}>
							{formatPrice(+props.PRICE)}
							{props.OLD_PRICE && props.OLD_PRICE > props.PRICE && <span>{formatPrice(+props.OLD_PRICE)}</span>}
						</span>
					</div>
					<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
						<Row gutter={[8, 18]}>
							<Col xs={24} md={8}>
								<div className={styles.formItem}>
									<Controller
										name="NAME"
										control={control}
										render={({ field }) => (
											<Input
												label="Имя"
												isLoading={userInfoIsLoading}
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
											/>
										)}
									/>
									{errors.NAME && <span className={styles.error}>{errors.NAME.message}</span>}
								</div>
							</Col>

							<Col xs={24} md={8}>
								<div className={styles.formItem}>
									<RHFEmailInput
										name="EMAIL"
										control={control}
										error={errors.EMAIL}
										label="Email"
										required={false}
										isLoading={userInfoIsLoading}
									/>
								</div>
							</Col>

							<Col xs={24} md={8}>
								<div className={styles.formField}>
									<RHFPhoneInput name="PHONE" control={control} error={errors.PHONE} isLoading={userInfoIsLoading} />
								</div>
							</Col>

							<Col xs={24}>
								<div className={styles.formField}>
									<Controller
										name="COMMENT"
										control={control}
										render={({ field }) => (
											<TextArea
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												placeholder={
													!isLegalUser
														? `Укажите в комментарии, если хотите совершить покупку по программе обмен в Трейд-ин, оформить рассрочку или оплатить заказ частями в Сплит.`
														: `Комментарий к заявке`
												}
												maxLength={500}
											/>
										)}
									/>
								</div>
							</Col>
						</Row>

						{showNewsletterCheckbox && (
							<div className={styles.checkboxes}>
								<NewsletterCheckbox checked={isNewsletterAccepted} onChange={setIsNewsletterAccepted} />
							</div>
						)}

						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginBottom: '2rem',
								marginTop: '2rem'
							}}>
							<SmartCaptcha sitekey={YANDEX_CAPTCHA_KEY} onSuccess={onChange} language={'ru'} />
						</div>
						<Button
							className={styles.button}
							id={props?.submitId || null}
							type="submit"
							theme="primary"
							isLoading={isLoading}
							disabled={isDisabled || !captcha}>
							Отправить
						</Button>

						{!!errorMessage && (
							<div className={styles.errorBlock}>
								<p className={styles.errorMessage}>Ошибка сервера. Попроуйте еще раз через минуту</p>
								<p className={styles.errorContact}>
									Не удалось отправить заявку?
									<button onClick={bitrixChat.open}>&nbsp;Напишите нам</button>
								</p>
							</div>
						)}

						<AgreementBlock />
					</form>
				</>
			)}
		</aside>
	)
}
